'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

export default function HomepageHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [rentOpen, setRentOpen] = useState(false);
  const [top10Open, setTop10Open] = useState(false);

  return (
    <header className="fixed top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-gray-100">
      <div className="flex items-center justify-between gap-4 h-16 md:h-20 px-6 md:px-12 lg:px-8 xl:px-12 2xl:px-20">
        <Link href="/" className="flex items-center shrink-0">
          <Image
            src="/spire-logo-black.webp"
            alt="Spire Group"
            width={180}
            height={60}
            className="h-12 md:h-16 lg:h-14 xl:h-16 w-auto"
            priority
          />
        </Link>
        <nav className="hidden lg:flex items-center lg:space-x-4 xl:space-x-7 2xl:space-x-10">
          {/* Buy Dropdown */}
          <div className="relative">
            <button
              onClick={() => setBuyOpen(!buyOpen)}
              className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase inline-flex items-center gap-1"
            >
              Buy
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${buyOpen ? 'rotate-180' : ''}`} />
            </button>
            {buyOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setBuyOpen(false)}></div>
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-3 z-50">
                  <Link href="/properties?listing_type=For Sale&property_type=condo" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setBuyOpen(false)}>
                    Condos for Sale
                  </Link>
                  <Link href="/properties?listing_type=For Sale&property_type=house_and_lot" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setBuyOpen(false)}>
                    Houses for Sale
                  </Link>
                  <Link href="/properties?listing_type=For Sale&property_type=townhouse" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setBuyOpen(false)}>
                    Townhouses for Sale
                  </Link>
                  <Link href="/properties?listing_type=For Sale&property_type=land" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setBuyOpen(false)}>
                    Lots for Sale
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link href="/top-10-houses" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setBuyOpen(false)}>
                    Luxury Properties
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Rent Dropdown */}
          <div className="relative">
            <button
              onClick={() => setRentOpen(!rentOpen)}
              className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase inline-flex items-center gap-1"
            >
              Rent
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${rentOpen ? 'rotate-180' : ''}`} />
            </button>
            {rentOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setRentOpen(false)}></div>
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-3 z-50">
                  <Link href="/properties?listing_type=For Rent&property_type=condo" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setRentOpen(false)}>
                    Condos for Rent
                  </Link>
                  <Link href="/properties?listing_type=For Rent&property_type=house_and_lot" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setRentOpen(false)}>
                    Houses for Rent
                  </Link>
                  <Link href="/properties?listing_type=For Rent&property_type=commercial" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setRentOpen(false)}>
                    Commercial for Rent
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link href="/top-10-condos-for-rent" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setRentOpen(false)}>
                    Luxury Rentals
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Top 10 Dropdown */}
          <div className="relative">
            <button
              onClick={() => setTop10Open(!top10Open)}
              className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase inline-flex items-center gap-1"
            >
              Top 10
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${top10Open ? 'rotate-180' : ''}`} />
            </button>
            {top10Open && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setTop10Open(false)}></div>
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-3 z-50">
                  <Link href="/top-10-houses" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setTop10Open(false)}>
                    Top 10 Houses
                  </Link>
                  <Link href="/top-10-condos" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setTop10Open(false)}>
                    Top 10 Condos
                  </Link>
                  <Link href="/top-10-houses-for-rent" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setTop10Open(false)}>
                    Top 10 Houses for Rent
                  </Link>
                  <Link href="/top-10-condos-for-rent" className="block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setTop10Open(false)}>
                    Top 10 Condos for Rent
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link href="/top-10-properties-makati" className="block px-5 py-3 text-sm font-medium text-[#d60500] hover:bg-[#d60500] hover:text-white transition-colors" onClick={() => setTop10Open(false)}>
                    Top 10 Makati
                  </Link>
                </div>
              </>
            )}
          </div>

          <Link href="/pre-selling" className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase">
            Pre-Selling
          </Link>
          <Link href="/commercial" className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase">
            Commercial
          </Link>
          <Link href="/neighborhoods" className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase">
            Neighborhoods
          </Link>
          <Link href="/blogs" className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase">
            Blog
          </Link>
          <Link href="/about" className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase">
            About
          </Link>
        </nav>
        <div className="hidden lg:flex items-center gap-4 xl:gap-6 shrink-0">
          <Link href="/login" className="text-[13px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors duration-300 uppercase whitespace-nowrap">
            Login
          </Link>
          <Button asChild className="bg-[#d60500] text-white hover:bg-white hover:text-[#d60500] border-2 border-[#d60500] px-4 xl:px-6 h-11 text-[13px] font-medium tracking-[0.05em] rounded-none transition-all duration-300 whitespace-nowrap">
            <Link href="/list-property">LIST YOUR PROPERTY</Link>
          </Button>
        </div>
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="px-6 md:px-12 py-6 space-y-3">
            {/* Buy Section - Mobile */}
            <div className="border-b border-gray-200 pb-3">
              <div className="px-2 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Buy Properties
              </div>
              <Link href="/properties?listing_type=For Sale&property_type=condo" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Condos for Sale
              </Link>
              <Link href="/properties?listing_type=For Sale&property_type=house_and_lot" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Houses for Sale
              </Link>
              <Link href="/properties?listing_type=For Sale&property_type=townhouse" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Townhouses for Sale
              </Link>
              <Link href="/properties?listing_type=For Sale&property_type=land" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Lots for Sale
              </Link>
              <Link href="/top-10-houses" className="block text-sm font-medium text-[#d60500] hover:bg-[#d60500]/10 px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Luxury Properties
              </Link>
            </div>

            {/* Rent Section - Mobile */}
            <div className="border-b border-gray-200 pb-3">
              <div className="px-2 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Rent Properties
              </div>
              <Link href="/properties?listing_type=For Rent&property_type=condo" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Condos for Rent
              </Link>
              <Link href="/properties?listing_type=For Rent&property_type=house_and_lot" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Houses for Rent
              </Link>
              <Link href="/properties?listing_type=For Rent&property_type=commercial" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Commercial for Rent
              </Link>
              <Link href="/top-10-condos-for-rent" className="block text-sm font-medium text-[#d60500] hover:bg-[#d60500]/10 px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Luxury Rentals
              </Link>
            </div>

            {/* Top 10 Section - Mobile */}
            <div className="border-b border-gray-200 pb-3">
              <div className="px-2 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Top 10 Luxury
              </div>
              <Link href="/top-10-houses" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Top 10 Houses
              </Link>
              <Link href="/top-10-condos" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Top 10 Condos
              </Link>
              <Link href="/top-10-houses-for-rent" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Top 10 Houses for Rent
              </Link>
              <Link href="/top-10-condos-for-rent" className="block text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Top 10 Condos for Rent
              </Link>
              <Link href="/top-10-properties-makati" className="block text-sm font-medium text-[#d60500] hover:bg-[#d60500]/10 px-2 py-2 rounded" onClick={() => setMobileMenuOpen(false)}>
                Top 10 Makati
              </Link>
            </div>

            <Link href="/pre-selling" className="block text-[14px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors uppercase py-2" onClick={() => setMobileMenuOpen(false)}>
              Pre-Selling
            </Link>
            <Link href="/commercial" className="block text-[14px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors uppercase py-2" onClick={() => setMobileMenuOpen(false)}>
              Commercial
            </Link>
            <Link href="/neighborhoods" className="block text-[14px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors uppercase py-2 border-t border-gray-200 pt-3 mt-3" onClick={() => setMobileMenuOpen(false)}>
              Neighborhoods
            </Link>
            <Link href="/blogs" className="block text-[14px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors uppercase py-2" onClick={() => setMobileMenuOpen(false)}>
              Blog
            </Link>
            <Link href="/about" className="block text-[14px] font-medium tracking-[0.1em] text-gray-600 hover:text-[#d60500] transition-colors uppercase py-2" onClick={() => setMobileMenuOpen(false)}>
              About
            </Link>

            {/* CTA Buttons - Mobile */}
            <div className="pt-4 pb-2 border-t border-gray-200 mt-3 space-y-3">
              <Button asChild variant="outline" className="w-full border-2 border-gray-300 text-gray-700 hover:border-[#d60500] hover:text-[#d60500] hover:bg-transparent h-12 text-[13px] font-medium tracking-[0.05em] rounded-none uppercase">
                <Link href="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              </Button>
              <Button asChild className="w-full bg-[#d60500] text-white hover:bg-white hover:text-[#d60500] border-2 border-[#d60500] h-12 text-[13px] font-medium tracking-[0.05em] rounded-none">
                <Link href="/list-property" onClick={() => setMobileMenuOpen(false)}>LIST YOUR PROPERTY</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
